import { useEffect, useId, useRef, type RefObject } from 'react'
import { X } from 'lucide-react'
import { LANDING_PAGE_NAV_ITEMS, LANDING_PAGE_ROUTE, LANDING_PAGE_UI } from '@propbix/constants/public'
import { Button, ButtonLink } from '@/components/Button'
import { ThemeToggle } from '@/components/ThemeToggle'
import { tenantAppHref } from '@/lib/tenant-app-url'

type MobileNavProps = {
  open: boolean
  onClose: () => void
  menuButtonRef: RefObject<HTMLButtonElement | null>
}

export function MobileNav({ open, onClose, menuButtonRef }: MobileNavProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const titleId = useId()

  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog === null) {
      return
    }
    if (open && !dialog.open) {
      dialog.showModal()
      document.body.style.overflow = 'hidden'
    } else if (!open && dialog.open) {
      dialog.close()
      document.body.style.overflow = ''
      menuButtonRef.current?.focus()
    }
  }, [open, menuButtonRef])

  useEffect(() => {
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  useEffect(() => {
    if (!open) {
      return
    }
    const dialog = dialogRef.current
    if (dialog === null) {
      return
    }
    const focusable = dialog.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    first?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key !== 'Tab' || focusable.length === 0) {
        return
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }
    dialog.addEventListener('keydown', onKeyDown)
    return () => dialog.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  return (
    <dialog
      ref={dialogRef}
      className="fixed inset-0 z-50 m-0 h-full max-h-none w-full max-w-none border-0 bg-transparent p-0 open:flex"
      aria-labelledby={titleId}
      onClose={onClose}
      onClick={(event) => {
        if (event.target === dialogRef.current) {
          onClose()
        }
      }}
    >
      <div className="ml-auto flex h-full w-full max-w-sm flex-col bg-background shadow-[var(--shadow-frame)]">
        <div className="flex items-center justify-between border-b border-line/10 px-5 py-4">
          <p id={titleId} className="text-base font-semibold text-ink">
            {LANDING_PAGE_UI.brandName}
          </p>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button
              variant="ghost"
              className="size-11 px-0"
              onClick={onClose}
              aria-label={LANDING_PAGE_UI.closeMenu}
            >
              <X size={20} strokeWidth={1.75} />
            </Button>
          </div>
        </div>

        <nav className="flex flex-1 flex-col gap-1 px-3 py-4" aria-label={LANDING_PAGE_UI.brandTagline}>
          {LANDING_PAGE_NAV_ITEMS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="rounded-[var(--radius-control)] px-3 py-3 text-base font-medium text-ink hover:bg-surface"
              onClick={onClose}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex flex-col gap-3 border-t border-line/10 px-5 py-5">
          <ButtonLink variant="secondary" href={tenantAppHref(LANDING_PAGE_ROUTE.login)} onClick={onClose}>
            {LANDING_PAGE_UI.loginCta}
          </ButtonLink>
          <ButtonLink variant="primary" href={tenantAppHref(LANDING_PAGE_ROUTE.signup)} onClick={onClose}>
            {LANDING_PAGE_UI.navCta}
          </ButtonLink>
        </div>
      </div>
    </dialog>
  )
}
